import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { RiArrowGoBackFill } from "react-icons/ri";

import { ProfileImages, ProjectDetailCommentContainer } from "../../../common/styled/project";
import Comment from "./comments/comment";
import InputField from "./comments/inputField";
import { targetWebSocket } from "../../../common/function/project";
import useUploadFiles from "../../../../../../../hooks/useUploadFiles";

const Comments = ({
  project,
  selectedRoom,
  setSelectedRoom,
  webSockets,
  comments,
  handleImageClick,
  handleContextMenu,
  isReply,
  selectedComment,
  resetSelectComment,
  setRoomConnection,
  newComment,
  setNewComment,
  participant,
}) => {
  const { projectNo } = useParams();
  const user = useSelector((state) => state?.user?.data[0]);
  const { uploadFiles, dataURItoBlob, readFileAsDataURL } = useUploadFiles();

  const textareaRef = useRef();
  const listRef = useRef();
  const MAX_LENGTH = 200;

  useEffect(() => {
    listRef.current && (listRef.current.scrollTop = listRef.current.scrollHeight);
  }, [comments, isReply, newComment.updateFiles]);

  useEffect(() => {
    textareaRef?.current && isReply && textareaRef.current.focus();
  }, [isReply]);

  const isCADFile = (name) =>
    [
      ".dwg",
      ".dxf",
      ".dwt",
      ".dws",
      ".dgn",
      ".dwf",
      ".stl",
      ".igs",
      ".step",
      ".3ds",
      ".prt",
      ".sat",
      ".sldprt",
      ".sldasm",
      ".slddrw",
    ].some((ext) => name.toLowerCase().endsWith(ext));

  const processFiles = async (files) => {
    const imgList = [],
      fileList = [];
    await Promise.all(
      Array.from(files).map(async (file) => {
        const filePath = await readFileAsDataURL(file);
        const entry = { fileNm: file.name, type: file.type, filePath, fileId: uuid() };
        (file.type.startsWith("image/") && !isCADFile(file.name) ? imgList : fileList).push(entry);
      }),
    );
    return { imgList, fileList };
  };

  const updateFilesFromInput = async (e) => {
    const { imgList, fileList } = await processFiles(e.target.files);
    setNewComment((prev) => ({
      ...prev,
      imgList: [...prev.imgList, ...imgList],
      fileList: [...prev.fileList, ...fileList],
      updateFiles: [...prev.updateFiles, ...imgList, ...fileList],
    }));
  };

  const uploadFilesToServer = async (files) => {
    const normalized = files.map((f) => {
      const blob = dataURItoBlob(f.filePath);
      blob.name = f.fileNm;
      return blob;
    });

    const uploaded = await uploadFiles(normalized);
    return uploaded?.map((f, i) => ({ ...f, originalName: files[i].fileNm })) || [];
  };

  const handleComment = async () => {
    if (!newComment.content?.trim() && !newComment.updateFiles.length) {
      toast.error("내용이 존재하지 않습니다.");
      return;
    }
    const target = targetWebSocket(webSockets.current, selectedRoom);
    if (!target || !project) return;

    const uploaded = await uploadFilesToServer(newComment.updateFiles);
    const update = { imgs: [], files: [], videos: [] };
    uploaded.forEach((f) => {
      const entry = { ...f, fileNm: f.originalName, filePath: f.fileName };
      if (f.fileType.startsWith("image") && !isCADFile(f.fileType)) update.imgs.push(entry);
      else if (f.fileType.startsWith("video")) update.videos.push(entry);
      else update.files.push(entry);
    });

    const body = {
      content: newComment.content,
      req: {
        projectNo,
        gbn: project.gbn,
        mberNo: user.mberNo,
        orgNo: project.gbn === "C" ? participant.orgNo : project.orgNo,
        content: newComment.content,
        roomId: selectedRoom.roomId,
        createMberId: user.userName,
        updateMberId: user.userName,
        delYn: "N",
        ...(selectedComment && isReply && { replyNo: selectedComment.projectCommentNo }),
      },
      ...update,
    };

    target.send(JSON.stringify(body));
    setNewComment({
      projectCommentNo: null,
      content: "",
      imgList: [],
      fileList: [],
      updateFiles: [],
      deleteFiles: [],
    });
    textareaRef.current.style.height = "auto";
    resetSelectComment();
  };

  const partCmpny = (orgInfos) => {
    const classes = ["one", "two", "three", "four"];
    return (
      <div className={classes[Math.min(orgInfos.length, 4) - 1]}>
        {orgInfos.slice(0, 4).map((info, i) => (
          <img
            key={i}
            src={info.orgImg ? imgHandler(info.orgImg) : "/img/function_22_bg.png"}
            alt={`cmpny${i + 1}`}
          />
        ))}
      </div>
    );
  };

  const handlePaste = async (e) => {
    for (let item of e.clipboardData.items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        await updateFilesFromInput({ target: { files: [file] } });
      }
    }
  };

  const preventDefault = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    updateFilesFromInput({ target: { files: e.dataTransfer.files } });
  };

  return (
    <ProjectDetailCommentContainer>
      <div className="projectFeedHeader">
        <div className="writerInfo">
          {selectedRoom?.orgInfos && (
            <ProfileImages>{partCmpny(selectedRoom.orgInfos)}</ProfileImages>
          )}
          <div>
            {selectedRoom?.orgInfos?.map((org, i) => (
              <span key={i}>
                {org.orgNm}
                {i !== selectedRoom.orgInfos.length - 1 && ", "}
              </span>
            ))}
          </div>
        </div>
        <div className="projectFeedHeaderButton">
          <button
            onClick={() => {
              setRoomConnection(selectedRoom.roomId);
              setSelectedRoom(null);
            }}>
            <RiArrowGoBackFill />
          </button>
        </div>
      </div>
      <input
        type="file"
        className="roomInput"
        id="fileUpload"
        onChange={updateFilesFromInput}
        onClick={preventDefault}
      />
      <label className="list" htmlFor="fileUpload" onDragOver={preventDefault} onDrop={handleDrop}>
        <ul ref={listRef}>
          {comments?.map((item) => (
            <Comment
              key={item.projectCommentNo}
              item={item}
              project={project}
              handleImageClick={handleImageClick}
              handleContextMenu={handleContextMenu}
              handleDeleteFile={(i) => {
                const type = i.type?.startsWith("image") ? "imgList" : "fileList";
                setNewComment((prev) => ({
                  ...prev,
                  [type]: prev[type].filter((f) => f.fileId !== i.fileId),
                  updateFiles: prev.updateFiles.filter((f) => f.fileId !== i.fileId),
                }));
              }}
              handleFindReply={(item) => {
                const el = document.querySelector(`.comment-${item.replyNo}`);
                el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                el?.animate(
                  [
                    { transform: "translateY(0px)" },
                    { transform: "translateY(3px)" },
                    { transform: "translateY(0px)" },
                    { transform: "translateY(-3px)" },
                    { transform: "translateY(0px)" },
                  ],
                  { duration: 200, iterations: 3, easing: "ease-in-out" },
                );
              }}
            />
          ))}
          <InputField
            project={project}
            isReply={isReply}
            selectedComment={selectedComment}
            resetSelectComment={resetSelectComment}
            newComment={newComment}
            handleDeleteFile={(item) => {
              const type = item.type?.startsWith("image") ? "imgList" : "fileList";
              setNewComment((prev) => ({
                ...prev,
                [type]: prev[type].filter((f) => f.fileId !== item.fileId),
                updateFiles: prev.updateFiles.filter((f) => f.fileId !== item.fileId),
              }));
            }}
            handleComment={handleComment}
            notParticipant={(e) => {
              e?.preventDefault();
              toast.error("프로젝트 참여자만 댓글을 남길 수 있습니다.");
            }}
            updateFilesFromInput={updateFilesFromInput}
            textareaRef={textareaRef}
            MAX_LENGTH={MAX_LENGTH}
            handleKeyDown={(e) => {
              if (!e.nativeEvent.isComposing && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleComment();
              }
            }}
            handleContentChange={(e) => {
              setNewComment((prev) => ({ ...prev, content: e.target.value }));
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
              listRef.current.scrollTop = listRef.current.scrollHeight;
            }}
            handlePaste={handlePaste}
          />
        </ul>
      </label>
    </ProjectDetailCommentContainer>
  );
};

export default Comments;
